import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import OnboardingChecklist from "../components/OnboardingChecklist";
import { RefModePicker, refModeText } from "../components/RefModePicker";
import { PresetBar, presetValues, presetText } from "../components/PresetBar";
import GenerateDialog from "../components/GenerateDialog";
import { FramePicker, GalleryPicker, uploadAsReference } from "../components/FramePicker";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Copy, Sparkles, Image, Video, Lock, Heart, Loader2, RefreshCw, Download, Eraser, Wand2, X, Images } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const ASPECT_RATIOS = ["1:1", "16:9", "9:16", "4:3", "3:2", "21:9"];
const IMAGE_CHIP_KEYS = ["chip_portrait", "chip_product", "chip_landscape", "chip_closeup", "chip_hero"];
const VIDEO_CHIP_KEYS = ["chip_dolly", "chip_tracking", "chip_static", "chip_slowmo", "chip_handheld"];
const DRAFT_KEY = "vixelforge_draft_v1";
const EMPTY_FORM = {
  obiettivo: "", soggetto: "", soggetti_extra: [], contesto: "", stile: "", mood: "",
  formato: "16:9", pubblico: "",
  movimento_soggetto: "", movimento_camera: "", durata: "", ritmo: "", continuita: "", dialogo_audio: "",
  negative_prompt: "", parametri_tecnici: "",
  camera_type: "", lens_type: "",
  reference_images: [],
  ref_mode: "edit", ref_keep: "", ref_change: "",
};

// Camera bodies commonly recognized by generative AI models (broad style hints)
const CAMERA_PRESETS = [
  "ARRI Alexa 65 (cinema)",
  "ARRI Alexa Mini LF (cinema)",
  "RED Komodo 6K (cinema)",
  "Sony FX6 (cinema)",
  "Sony A7R V (mirrorless)",
  "Canon EOS R5 (mirrorless)",
  "Canon 5D Mark IV (DSLR)",
  "Nikon Z9 (mirrorless)",
  "Fujifilm X-T5 (mirrorless)",
  "Leica Q3 (compact premium)",
  "Hasselblad H6D (medium format)",
  "Phase One XT (medium format)",
  "iPhone 15 Pro (smartphone)",
  "16mm film camera",
  "35mm film camera",
];

// Lens presets covering focal ranges, apertures and characterful glass
const LENS_PRESETS = [
  "24mm f/1.4 (wide, cinematic)",
  "35mm f/1.4 (documentary)",
  "50mm f/1.2 (natural, portrait)",
  "85mm f/1.4 (portrait, bokeh)",
  "100mm macro f/2.8",
  "135mm f/1.8 (compression portrait)",
  "200mm f/2.8 (telephoto)",
  "14-24mm f/2.8 (ultrawide zoom)",
  "24-70mm f/2.8 (standard zoom)",
  "70-200mm f/2.8 (telephoto zoom)",
  "40mm T2.2 anamorphic (cinematic)",
  "24mm tilt-shift (architecture)",
  "8mm fisheye (creative)",
  "50mm vintage Zeiss Planar",
];

const TEMPLATES = [
  {
    id: "product_photo",
    label: { en: "Product photo", it: "Foto prodotto", es: "Foto de producto", fr: "Photo produit", de: "Produktfoto" },
    outputType: "image",
    form: { obiettivo: "Product photography for e-commerce listing", soggetto: "Premium product centered on a soft gradient backdrop", contesto: "Studio setup, seamless background", stile: "photographic", mood: "elegante", formato: "1:1", pubblico: "" },
  },
  {
    id: "hero_shot",
    label: { en: "Hero shot", it: "Scena principale", es: "Escena principal", fr: "Plan principal", de: "Hauptszene" },
    outputType: "image",
    form: { obiettivo: "Landing page hero image for a launch campaign", soggetto: "A confident subject centered in a cinematic scene", contesto: "Wide environment with atmospheric depth", stile: "cinematographic", mood: "epic", formato: "16:9", pubblico: "" },
  },
  {
    id: "character_portrait",
    label: { en: "Character portrait", it: "Ritratto personaggio", es: "Retrato de personaje", fr: "Portrait personnage", de: "Charakter-Porträt" },
    outputType: "image",
    form: { obiettivo: "Editorial character portrait", soggetto: "A character with strong personality, waist-up composition, direct eye contact", contesto: "Neutral studio background with dramatic side light", stile: "photographic", mood: "elegant", formato: "3:2", pubblico: "" },
  },
  {
    id: "product_ad_video",
    label: { en: "Product ad video", it: "Video pubblicitario prodotto", es: "Vídeo publicitario", fr: "Vidéo publicitaire", de: "Produkt-Ad Video" },
    outputType: "video",
    form: { obiettivo: "Short social ad revealing a new product", soggetto: "The product rotating slowly under focused lighting", contesto: "Minimal studio setting with soft light beams", stile: "cinematographic", mood: "vibrante", formato: "9:16", pubblico: "",
      movimento_soggetto: "Slow subtle rotation", movimento_camera: "Slow dolly-in to hero close-up", durata: "6s", ritmo: "energico", continuita: "single continuous take", dialogo_audio: "Upbeat royalty-free electronic beat" },
  },
];
const STYLE_KEYS = { en: ["photographic","cinematic","illustration","3D render","anime","watercolor","cyberpunk","minimalist"], it: ["fotografico","cinematografico","illustrazione","3D render","anime","watercolor","cyberpunk","minimalista"], es: ["fotográfico","cinematográfico","ilustración","3D render","anime","acuarela","cyberpunk","minimalista"], fr: ["photographique","cinématographique","illustration","3D render","anime","aquarelle","cyberpunk","minimaliste"], de: ["fotografisch","filmisch","Illustration","3D-Render","Anime","Aquarell","Cyberpunk","minimalistisch"] };
const MOOD_KEYS = { en: ["epic","melancholic","vibrant","serene","mysterious","dreamlike","elegant"], it: ["epico","malinconico","vibrante","sereno","misterioso","onirico","elegante"], es: ["épico","melancólico","vibrante","sereno","misterioso","onírico","elegante"], fr: ["épique","mélancolique","vibrant","serein","mystérieux","onirique","élégant"], de: ["episch","melancholisch","lebhaft","gelassen","mysteriös","traumhaft","elegant"] };

const EDIT_MEDIA_L = {
  it: { title: "Modifica media", hint: "Modifica i campi del prompt qui sotto e premi Genera per rigenerare.", gallery: "Gallery →" },
  en: { title: "Edit media", hint: "Edit the prompt fields below and press Generate to re-render.", gallery: "Gallery →" },
};

function EditMediaPanel({ media, lang, onClose }) {
  const tt = EDIT_MEDIA_L[lang] || EDIT_MEDIA_L.en;
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    let revoked = false;
    let obj;
    (async () => {
      try {
        const r = await api.get(`/generation/${media.generation_id}/thumbnail`, { responseType: "blob" });
        obj = URL.createObjectURL(r.data);
        if (!revoked) setThumb(obj);
      } catch { /* no thumb */ void 0; }
    })();
    return () => { revoked = true; if (obj) URL.revokeObjectURL(obj); };
  }, [media.generation_id]);
  return (
    <section className="rounded-xl border border-orange-500/30 bg-orange-500/[0.04] p-4" data-testid="edit-media-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold">{tt.title}</div>
        <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="close-edit-media"><X className="w-4 h-4" /></button>
      </div>
      <div className="rounded-lg overflow-hidden border border-white/10 bg-[#0E0E12] aspect-video flex items-center justify-center">
        {thumb ? <img src={thumb} alt="" className="w-full h-full object-contain" /> : <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-zinc-300 font-semibold flex items-center gap-1.5">
          {media.type === "video" ? <Video className="w-3.5 h-3.5" /> : <Image className="w-3.5 h-3.5" />}{media.model_name}
        </span>
        <Link to="/app/gallery" className="text-orange-400 hover:underline">{tt.gallery}</Link>
      </div>
      <p className="text-[11px] text-zinc-500 mt-2">{tt.hint}</p>
    </section>
  );
}

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [outputType, setOutputType] = useState("image");
  const [models, setModels] = useState({ image: [], video: [] });
  const [modelId, setModelId] = useState("");
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [refGalleryOpen, setRefGalleryOpen] = useState(false);
  const frameFromRefs = (role) => {
    if (outputType !== "video") return null;
    const f = (form.video_frames || {})[role === "start_frame" ? "start" : "end"];
    return f ? { kind: f.kind, id: f.id } : null;
  };
  const frameTT = {
    frame_start: t("ref_role_start"), frame_end: t("ref_role_end"), frame_upload: t("frame_upload"), frame_from_video: t("frame_from_video"), frame_pick_video: t("frame_pick_video"), frame_grab_title: t("frame_grab_title"), frame_grab_hint: t("frame_grab_hint"), frame_grab_first: t("frame_grab_first"), frame_grab_last: t("frame_grab_last"), frame_grab_other: t("frame_grab_other"), frame_grab_use: t("frame_grab_use"),
    frame_from_gallery: t("frame_from_gallery"), frame_pick_gallery: t("frame_from_gallery"), frame_gallery_empty: t("frame_gallery_empty"),
    frame_too_large: t("ref_too_large"), frames_hint: t("frames_prompt_note"), frames_hint_noend: t("frames_prompt_note"),
  };
  const setFrame = (which, f) => set("video_frames", { ...(form.video_frames || {}), [which]: f ? { kind: f.kind, id: f.id } : null,
    ...(which === "start" && !f ? { end: null } : {}) });
  const [editMedia, setEditMedia] = useState(null);
  const [extGenOpen, setExtGenOpen] = useState(false);
  const [tweak, setTweak] = useState("");
  const [refining, setRefining] = useState(false);
  const [uploadingRef, setUploadingRef] = useState(false);
  const [form, setForm] = useState(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && d.form) return { ...EMPTY_FORM, ...d.form };
      }
    } catch {}
    return EMPTY_FORM;
  });

  // Feedback when returning from the Google Drive consent redirect (mobile Safari same-tab flow)
  useEffect(() => {
    const d = params.get("drive");
    if (!d) return;
    const it = lang === "it";
    if (d === "connected") toast.success(it ? "Google Drive collegato ✓" : "Google Drive connected ✓");
    else if (d === "scope_error") toast.error(it ? "Serve il permesso di salvare su Drive. Riprova e consenti l'accesso." : "Drive save permission is required. Retry and allow access.");
    else if (d === "drive_api_disabled") toast.error(it ? "La Google Drive API non è abilitata nel progetto Google Cloud." : "Google Drive API is not enabled in your Google Cloud project.");
    else toast.error(it ? "Collegamento a Google Drive non riuscito. Riprova." : "Google Drive connection failed. Please try again.");
    navigate("/app", { replace: true });
    // eslint-disable-next-line
  }, []);


  // Load draft outputType + modelId once
  useEffect(() => {
    // Edit-from-history: ?edit=<prompt_id> takes precedence over draft
    const editId = params.get("edit");
    const mediaId = params.get("media");
    const keep = mediaId ? `/app?media=${mediaId}` : "/app";
    if (editId) {
      (async () => {
        try {
          const { data } = await api.get("/prompts", { params: { limit: 200 } });
          const found = data.find((p) => p.prompt_id === editId);
          if (found && found.input_data) {
            const inp = found.input_data;
            setForm({ ...EMPTY_FORM, ...inp, reference_images: (inp.reference_images || []).filter((r) => !mediaId || r.generation_id !== mediaId) });
            setOutputType(inp.output_type || "image");
            if (inp.model_id) setModelId(inp.model_id);
            toast.success(t("edit_loaded"));
            navigate(keep, { replace: true });
            return;
          }
        } catch {}
        navigate(keep, { replace: true });
      })();
      return;
    }
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d?.outputType) setOutputType(d.outputType);
        if (d?.modelId) setModelId(d.modelId);
        const hasAny = d?.form && Object.values(d.form).some((v) => (Array.isArray(v) ? v.length : v));
        if (hasAny) toast.success(t("draft_restored"));
      }
    } catch {}
    // eslint-disable-next-line
  }, []);

  // Autosave draft on any change
  useEffect(() => {
    const handle = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, outputType, modelId }));
      } catch {}
    }, 300);
    return () => clearTimeout(handle);
  }, [form, outputType, modelId]);

  // Load the media being edited (?media=<generation_id>) for the side-by-side editor
  useEffect(() => {
    const d = params.get("drive");
    if (d === "connected") toast.success("Google Drive " + (t("drive_connected_ok") || "collegato"));
    else if (d === "error" || d === "scope_error") toast.error("Google Drive: " + (t("drive_error") || "collegamento non riuscito"));
    if (d) navigate("/app", { replace: true });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const mediaId = params.get("media");
    if (!mediaId) { setEditMedia(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/generation/${mediaId}`);
        setEditMedia(data);
        // Only the ORIGINAL inputs are reused: the media being edited is shown side by side, never attached as reference.
        if (data.type === "video" && data.start_frame) {
          setForm((f) => (f.video_frames?.start ? f : { ...f, video_frames: { ...(f.video_frames || {}), start: { kind: "gallery", id: data.start_frame } } }));
        }
      } catch { setEditMedia(null); }
    })();
  }, [params]);

  const isPending = user?.plan === "pending";
  const isPro = user?.plan === "pro";
  const isPlus = user?.plan === "plus";
  const canUseAdvanced = isPlus || isPro;
  const styles = STYLE_KEYS[lang] || STYLE_KEYS.en;
  const moods = MOOD_KEYS[lang] || MOOD_KEYS.en;

  useEffect(() => {
    api.get("/models/available").then((r) => setModels(r.data));
    api.get("/quota").then((r) => setQuota(r.data));
  }, [user?.plan]);
  useEffect(() => { setModelId(""); }, [outputType]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [activePreset, setActivePreset] = useState(null);
  const applyPreset = (p) => {
    setOutputType("image");
    setForm((f) => ({ ...f, ...presetValues(p, lang) }));
    setActivePreset(p.id);
    const pt = presetText(lang);
    if (p.photo) {
      toast.success(isPending ? pt.photo_plan : pt.photo_hint);
      if (!isPending) setTimeout(() => document.querySelector('[data-testid="ref-images-section"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 250);
    } else toast.success(pt.done);
    setTimeout(() => document.querySelector('[data-testid="input-soggetto"]')?.focus({ preventScroll: p.photo && !isPending }), 300);
  };

  const clearForm = () => {
    if (!window.confirm(t("clear_form_confirm"))) return;
    setForm(EMPTY_FORM);
    setActivePreset(null);
    setResult(null);
    setTweak("");
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ void 0; }
    toast.success(t("clear_form"));
  };

  const canGenerate = !!form.obiettivo && !!form.soggetto && (isPending || !!modelId);

  const handleGenerateClick = () => {
    if (loading) return;
    if (overQuota) { toast.error(t("limit_msg")); return; }
    if (!form.obiettivo) {
      toast.error(t("goal") + " " + t("required_field"));
      document.querySelector('[data-testid="input-obiettivo"]')?.focus();
      return;
    }
    if (!form.soggetto) {
      toast.error(t("subject") + " " + t("required_field"));
      document.querySelector('[data-testid="input-soggetto"]')?.focus();
      return;
    }
    if (!isPending && !modelId) {
      toast.error(t("select_model_first"));
      document.querySelector(`[data-testid="output-type-${outputType}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    generate();
  };

  const generate = async () => {
    setLoading(true);
    setResult(null);
    try {
      const payload = { output_type: outputType, model_id: isPending ? null : modelId, ...form, ref_mode: form.ref_mode || "edit" };
      const { data } = await api.post("/prompts/generate", payload);
      setResult(data);
      const q = await api.get("/quota"); setQuota(q.data);
      const me = await api.get("/auth/me"); setUser(me.data);
      toast.success(t("prompt_ok"));
      // Clear draft after successful generation
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
    } catch (err) {
      toast.error(errorMessage(err, t));
    } finally {
      setLoading(false);
    }
  };

  const copy = () => { navigator.clipboard.writeText(result.generated_prompt); toast.success(t("copied")); };

  const saveTitle = async (newTitle) => {
    if (!result?.prompt_id) return;
    const clean = (newTitle || "").trim().slice(0, 120);
    if (clean === (result.title || "")) return;
    try {
      await api.patch(`/prompts/${result.prompt_id}`, { title: clean });
      setResult({ ...result, title: clean });
    } catch (err) { toast.error(errorMessage(err, t)); }
  };
  const toggleFav = async () => {
    if (isPending) { toast.error(t("fav_locked")); return; }
    try {
      const { data } = await api.post(`/prompts/${result.prompt_id}/favorite`);
      setResult((r) => ({ ...r, is_favorite: data.is_favorite }));
      toast.success(data.is_favorite ? t("saved_fav") : t("unsaved_fav"));
    } catch (err) { toast.error(errorMessage(err, t)); }
  };

  const refine = async () => {
    if (!result) return;
    setRefining(true);
    try {
      const { data } = await api.post(`/prompts/${result.prompt_id}/refine`, { tweak });
      setResult(data);
      setTweak("");
      const q = await api.get("/quota"); setQuota(q.data);
      const me = await api.get("/auth/me"); setUser(me.data);
      toast.success(t("refined_ok"));
    } catch (err) {
      toast.error(errorMessage(err, t));
    } finally {
      setRefining(false);
    }
  };

  const exportJson = async () => {
    if (!result) return;
    if (!isPro) { toast.error(t("export_pro_only")); return; }
    try {
      const { data } = await api.get(`/prompts/${result.prompt_id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vixelforge-${result.prompt_id}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errorMessage(err, t));
    }
  };

  const overQuota = quota && typeof quota.quota === "number"
    && quota.used >= quota.quota
    && (quota.credits || 0) === 0
    && (quota.signup_bonus_remaining || 0) === 0;
  const availableModels = outputType === "image" ? models.image : models.video;

  // Live preview: mirror of backend build_user_message (English labels)
  const livePreview = (() => {
    const parts = [];
    if (form.obiettivo) parts.push(`Goal: ${form.obiettivo}`);
    parts.push(`Output type: ${outputType}`);
    if (form.soggetto) parts.push(`Main subject: ${form.soggetto}`);
    if (form.contesto) parts.push(`Context/environment: ${form.contesto}`);
    if (form.stile) parts.push(`Visual style: ${form.stile}`);
    if (form.mood) parts.push(`Mood/atmosphere: ${form.mood}`);
    if (form.formato) parts.push(`Format / aspect ratio: ${form.formato}`);
    if (form.pubblico) parts.push(`Target audience: ${form.pubblico}`);
    if (outputType === "video") {
      if (form.movimento_soggetto) parts.push(`Subject motion: ${form.movimento_soggetto}`);
      if (form.movimento_camera) parts.push(`Camera motion: ${form.movimento_camera}`);
      if (form.durata) parts.push(`Duration: ${form.durata}`);
      if (form.ritmo) parts.push(`Pace: ${form.ritmo}`);
      if (form.continuita) parts.push(`Continuity: ${form.continuita}`);
      if (form.dialogo_audio) parts.push(`Dialog/audio: ${form.dialogo_audio}`);
    }
    if (isPlus || isPro) {
      if (form.negative_prompt) parts.push(`Elements to avoid: ${form.negative_prompt}`);
      if (form.camera_type) parts.push(`Camera body: ${form.camera_type}`);
      if (form.lens_type) parts.push(`Lens / optics: ${form.lens_type}`);
      if (form.parametri_tecnici) parts.push(`Extra technical parameters: ${form.parametri_tecnici}`);
    }
    return form.obiettivo || form.soggetto ? parts.join("\n") : "";
  })();

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      {isPending && (
        <div className="mb-8 rounded-lg border border-orange-500/30 bg-orange-500/[0.05] p-6 flex items-center justify-between flex-wrap gap-4" data-testid="pending-banner">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-1">
              {(user?.signup_bonus_remaining ?? 0) > 0 ? (t("free_trial") || "Free trial") : t("no_plan_label")}
            </div>
            <div className="text-lg font-bold text-white">
              {(user?.signup_bonus_remaining ?? 0) > 0
                ? (t("free_trial_headline")?.replace("{n}", user.signup_bonus_remaining) || `You have ${user.signup_bonus_remaining} free credits left`)
                : t("choose_plan_to_start")}
            </div>
            <div className="text-sm text-zinc-400 mt-1">
              {(user?.signup_bonus_remaining ?? 0) > 0
                ? (t("free_trial_sub") || "Use them to try the generator, then upgrade to keep crafting.")
                : t("choose_plan_hint")}
            </div>
          </div>
          <Link to="/app/account">
            <Button data-testid="pending-choose-plan-btn" className="bg-orange-500 hover:bg-orange-600 text-black font-bold rounded-full h-11 px-6">
              {t("choose_plan")}
            </Button>
          </Link>
        </div>
      )}
      <OnboardingChecklist />
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#121218] mb-8">
        <div className="absolute right-0 top-0 h-full w-1/2 pointer-events-none opacity-30" style={{
          background: "radial-gradient(ellipse at 80% 30%, rgba(249,115,22,0.25), transparent 60%)"
        }} />
        <div className="relative flex items-start justify-between flex-wrap gap-4 p-6 md:p-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-2">{t("generator")}</div>
            <h1 className="font-black text-4xl md:text-5xl tracking-tight text-[#F4EFEB]">{t("forge_title")}</h1>
            <Button onClick={() => setExtGenOpen(true)} variant="outline" size="sm"
              className="mt-3 rounded-full border-orange-500/30 bg-transparent text-orange-300 hover:bg-orange-500/10" data-testid="external-prompt-btn">
              <Wand2 className="w-4 h-4 mr-2" /> {t("external_prompt")}
            </Button>
            <GenerateDialog open={extGenOpen} onOpenChange={setExtGenOpen} promptText="" promptId={null} outputType="image" editablePrompt={true} />
          </div>
          {quota && (
          <div className="rounded-md border border-white/10 bg-white/[0.02] px-4 py-3" data-testid="quota-display">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
              {isPending ? (t("free_trial") || "Free trial") : t("monthly_usage")}
            </div>
            {isPending ? (
              <div className="text-lg font-black" data-testid="signup-bonus-badge">
                {quota.signup_bonus_remaining ?? 0}{" "}
                <span className="text-zinc-500 font-normal text-xs">{t("free_ai_prompts_left") || "credits left"}</span>
              </div>
            ) : (
              <div className="text-lg font-black">
                {quota.used} <span className="text-zinc-500 font-normal">/ {quota.quota}</span>
              </div>
            )}
            {user?.bonus_prompts > 0 && (
              <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mt-1" data-testid="bonus-badge">
                +{user.bonus_prompts} {t("bonus_label")}
              </div>
            )}
            {quota.credits > 0 && (
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mt-1" data-testid="credits-badge">
                +{quota.credits} {t("credits_label") || "credits"}
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {overQuota && (
        <div className="mb-6 rounded-md border border-orange-500/30 bg-orange-500/5 p-5 flex items-center justify-between flex-wrap gap-4" data-testid="paywall-banner">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-1">{t("limit_reached")}</div>
            <div className="text-sm text-zinc-300">{t("limit_msg")}</div>
          </div>
          <Link to="/app/account"><Button className="bg-orange-500 hover:bg-orange-600 text-black font-bold rounded-full" data-testid="upgrade-cta-btn">{t("upgrade")}</Button></Link>
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          {editMedia && (
            <EditMediaPanel
              media={editMedia}
              lang={lang}
              onClose={() => { setEditMedia(null); navigate("/app", { replace: true }); }}
            />
          )}
          {/* Prompt templates quick-start */}
          <section data-testid="templates-row">
            <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-3">{t("templates_label")}</div>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  data-testid={`template-${tpl.id}`}
                  onClick={() => {
                    setOutputType(tpl.outputType);
                    setForm({ ...EMPTY_FORM, ...tpl.form });
                    setResult(null);
                    toast.success(t("template_applied"));
                  }}
                  className="px-4 py-2 rounded-full bg-white/[0.03] hover:bg-orange-500/10 border border-white/10 hover:border-orange-500/40 text-sm text-zinc-300 hover:text-orange-400 transition-colors duration-150"
                >
                  {tpl.label[lang] || tpl.label.en}
                </button>
              ))}
            </div>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">{t("step_of",{n:1})} · {t("output_type")}</div>
            <div className="grid grid-cols-2 gap-3">
              {[{ k: "image", l: t("image"), i: Image }, { k: "video", l: t("video"), i: Video }].map((o) => (
                <button
                  key={o.k}
                  onClick={() => setOutputType(o.k)}
                  data-testid={`output-type-${o.k}`}
                  className={`p-6 rounded-md border transition-colors duration-200 text-left ${outputType === o.k ? "border-orange-500 bg-orange-500/5" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}
                >
                  <o.i className={`w-6 h-6 mb-3 ${outputType === o.k ? "text-orange-400" : "text-zinc-400"}`} />
                  <div className="font-black text-lg tracking-tight">{o.l}</div>
                </button>
              ))}
            </div>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">{t("step_of",{n:2})} · {t("model")}</div>
            {isPending ? (
              <div className="rounded-md border border-white/10 bg-white/[0.02] p-5 flex items-center gap-3">
                <Lock className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">{t("pending_universal_title")}</div>
                  <div className="text-xs text-zinc-500">{t("pending_universal_desc")}</div>
                </div>
                <Link to="/app/account"><Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-black font-bold rounded-full" data-testid="upgrade-small-btn">{t("choose_plan")}</Button></Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {availableModels.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setModelId(m.id)}
                    data-testid={`model-${m.id}`}
                    className={`p-3 rounded-md border text-left transition-colors duration-200 ${modelId === m.id ? "border-orange-500 bg-orange-500/5" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}
                  >
                    <div className="text-sm font-bold truncate">{m.name}</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-0.5">{m.provider}</div>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">{t("step_of",{n:3})} · {t("questionnaire")}</div>
              <button
                type="button"
                data-testid="clear-form-btn"
                onClick={clearForm}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-zinc-500 hover:text-orange-400 transition-colors duration-150"
              >
                <Eraser className="w-3.5 h-3.5" />
                {t("clear_form")}
              </button>
            </div>
            <div className="space-y-4 rounded-md border border-white/10 bg-white/[0.02] p-5">
              <PresetBar active={activePreset} onApply={applyPreset} />
              <Field label={t("goal")} required>
                <AutoTextarea testid="input-obiettivo" placeholder={t("goal_ph")} value={form.obiettivo} onChange={(e) => set("obiettivo", e.target.value)} />
              </Field>
              <Field label={t("subject")} required>
                <AutoTextarea testid="input-soggetto" placeholder={t("subject_ph")} value={form.soggetto} onChange={(e) => set("soggetto", e.target.value)} />
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="subject-chips">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold self-center mr-1">{t("quick_fill")}:</span>
                  {(outputType === "video" ? VIDEO_CHIP_KEYS : IMAGE_CHIP_KEYS).map((k) => {
                    const label = t(k);
                    return (
                      <button
                        key={k}
                        type="button"
                        data-testid={`chip-${k}`}
                        onClick={() => set("soggetto", form.soggetto ? `${form.soggetto.trimEnd()}, ${label}` : label)}
                        className="px-2.5 py-1 text-xs rounded-full bg-white/5 hover:bg-orange-500/15 border border-white/10 hover:border-orange-500/40 text-zinc-300 hover:text-orange-400 transition-colors duration-150"
                      >
                        + {label}
                      </button>
                    );
                  })}
                </div>
                {(() => {
                  const limits = { pending: 0, basic: 0, plus: 2, pro: 4 };
                  const maxExtras = limits[user?.plan] ?? 0;
                  const extras = form.soggetti_extra || [];
                  if (maxExtras === 0) return null;
                  const toObj = (v) => (typeof v === "string" ? { name: v, pose: "", position: "", size: "" } : (v || { name: "", pose: "", position: "", size: "" }));
                  return (
                    <div className="mt-3 space-y-3" data-testid="extra-subjects">
                      {extras.map((raw, i) => {
                        const val = toObj(raw);
                        const patch = (k, nv) => {
                          const next = [...extras]; next[i] = { ...val, [k]: nv };
                          set("soggetti_extra", next);
                        };
                        return (
                          <div key={i} className="rounded-md border border-white/10 bg-white/[0.02] p-3 space-y-2" data-testid={`extra-subject-card-${i}`}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">{t("extra_subject")} #{i + 2}</span>
                              <button
                                type="button"
                                data-testid={`remove-extra-${i}`}
                                onClick={() => set("soggetti_extra", extras.filter((_, x) => x !== i))}
                                className="text-xs text-zinc-500 hover:text-red-400"
                              >
                                {t("remove_subject")}
                              </button>
                            </div>
                            <Input
                              data-testid={`extra-subject-${i}`}
                              value={val.name}
                              onChange={(e) => patch("name", e.target.value)}
                              placeholder={t("subj_name_ph") || "e.g. tabby cat, wizard, sports car"}
                              className="bg-[#121218] border-white/10 focus:ring-orange-500"
                            />
                            <div className="grid grid-cols-3 gap-2">
                              <Input
                                data-testid={`extra-pose-${i}`}
                                value={val.pose || ""}
                                onChange={(e) => patch("pose", e.target.value)}
                                placeholder={t("subj_pose_ph") || "pose (sitting, running…)"}
                                className="bg-[#121218] border-white/10 focus:ring-orange-500 text-xs"
                              />
                              <Input
                                data-testid={`extra-position-${i}`}
                                value={val.position || ""}
                                onChange={(e) => patch("position", e.target.value)}
                                placeholder={t("subj_position_ph") || "position (left, background…)"}
                                className="bg-[#121218] border-white/10 focus:ring-orange-500 text-xs"
                              />
                              <Input
                                data-testid={`extra-size-${i}`}
                                value={val.size || ""}
                                onChange={(e) => patch("size", e.target.value)}
                                placeholder={t("subj_size_ph") || "size (small, dominant…)"}
                                className="bg-[#121218] border-white/10 focus:ring-orange-500 text-xs"
                              />
                            </div>
                          </div>
                        );
                      })}
                      {extras.length < maxExtras && (
                        <button
                          type="button"
                          data-testid="add-subject-btn"
                          onClick={() => set("soggetti_extra", [...extras, { name: "", pose: "", position: "", size: "" }])}
                          className="text-xs rounded-full px-3 py-1.5 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-400 font-semibold"
                        >
                          + {t("add_subject")} ({extras.length + 1}/{maxExtras + 1})
                        </button>
                      )}
                    </div>
                  );
                })()}
              </Field>
              <Field label={t("context_env")}>
                <AutoTextarea testid="input-contesto" placeholder={t("context_ph")} value={form.contesto} onChange={(e) => set("contesto", e.target.value)} />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label={t("style")}>
                  <Select value={form.stile} onValueChange={(v) => set("stile", v)}>
                    <SelectTrigger data-testid="select-stile" className="bg-[#121218] border-white/10"><SelectValue placeholder={t("style_ph")} /></SelectTrigger>
                    <SelectContent className="bg-[#16161D] border-white/10">
                      {styles.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("mood")}>
                  <Select value={form.mood} onValueChange={(v) => set("mood", v)}>
                    <SelectTrigger data-testid="select-mood" className="bg-[#121218] border-white/10"><SelectValue placeholder={t("mood_ph")} /></SelectTrigger>
                    <SelectContent className="bg-[#16161D] border-white/10">
                      {moods.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label={t("aspect")}>
                  <Select value={form.formato} onValueChange={(v) => set("formato", v)}>
                    <SelectTrigger data-testid="select-formato" className="bg-[#121218] border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#16161D] border-white/10">
                      {ASPECT_RATIOS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("audience")}>
                  <Input data-testid="input-pubblico" placeholder={t("audience_ph")} value={form.pubblico} onChange={(e) => set("pubblico", e.target.value)} className="bg-[#121218] border-white/10 focus:ring-orange-500" />
                </Field>
              </div>

              {user?.plan && user?.plan !== "pending" && (
                <div className="pt-4 border-t border-white/10 space-y-3" data-testid="ref-images-section">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold">{t("ref_images_label")}</div>
                  <div className="text-xs text-zinc-500">{t("ref_images_hint")}</div>
                  <div className="grid grid-cols-3 gap-2">
                    {(form.reference_images || []).map((img, i) => (
                      <div key={i} className="relative rounded-md overflow-hidden border border-white/10" data-testid={`ref-img-${i}`}>
                        <RefImageThumb img={img} />
                        <select
                          value={img.role}
                          onChange={(e) => {
                            const next = [...form.reference_images]; next[i] = { ...next[i], role: e.target.value };
                            set("reference_images", next);
                          }}
                          data-testid={`ref-role-${i}`}
                          className="absolute top-1 left-1 text-[10px] bg-[#0E0E12]/70 text-white border border-white/20 rounded px-1 py-0.5"
                        >
                          <option value="subject">{t("ref_role_subject")}</option>
                          <option value="environment">{t("ref_role_env")}</option>
                        </select>
                        <button
                          type="button"
                          data-testid={`ref-remove-${i}`}
                          onClick={async () => {
                            const removed = form.reference_images[i];
                            set("reference_images", form.reference_images.filter((_, x) => x !== i));
                            if (removed?.image_id) {
                              try { await api.delete(`/reference-images/${removed.image_id}`); } catch { /* ignore */ }
                            }
                          }}
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#0E0E12]/70 text-white text-[10px] hover:bg-red-500"
                        >×</button>
                      </div>
                    ))}
                    {(form.reference_images || []).length < 3 && (
                      <button type="button" onClick={() => setRefGalleryOpen(true)} data-testid="ref-from-gallery-btn"
                        className="rounded-md border border-dashed border-white/20 hover:border-orange-500/40 bg-white/[0.02] h-24 flex flex-col items-center justify-center gap-1 text-xs text-zinc-400 hover:text-orange-400 transition-colors">
                        <Images className="w-4 h-4" /> {t("frame_from_gallery")}
                      </button>
                    )}
                    <GalleryPicker open={refGalleryOpen} onClose={() => setRefGalleryOpen(false)} tt={frameTT}
                      onPick={(f) => { setRefGalleryOpen(false); set("reference_images", [...(form.reference_images || []), { role: "subject", generation_id: f.id }].slice(0, 3)); }} />
                    {(form.reference_images || []).length < 3 && (
                      <label
                        className="rounded-md border border-dashed border-white/20 hover:border-orange-500/40 bg-white/[0.02] h-24 flex items-center justify-center cursor-pointer text-xs text-zinc-400 hover:text-orange-400"
                        data-testid="ref-upload-label"
                      >
                        {uploadingRef ? <Loader2 className="w-4 h-4 animate-spin" /> : "+ Upload"}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          multiple
                          data-testid="ref-upload-input"
                          className="hidden"
                          disabled={uploadingRef}
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            const room = 3 - (form.reference_images || []).length;
                            const picked = files.slice(0, room);
                            e.target.value = "";
                            if (picked.length === 0) return;
                            setUploadingRef(true);
                            const uploaded = [];
                            for (const f of picked) {
                              if (f.size > 2 * 1024 * 1024) { toast.error(t("ref_too_large") || "Max 2 MB per image"); continue; }
                              try {
                                const fd = new FormData();
                                fd.append("file", f);
                                const { data } = await api.post("/reference-images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                uploaded.push({ role: "subject", image_id: data.image_id });
                              } catch (err) {
                                toast.error(errorMessage(err, t));
                              }
                            }
                            setUploadingRef(false);
                            if (uploaded.length) {
                              set("reference_images", [...(form.reference_images || []), ...uploaded]);
                            }
                          }}
                        />
                      </label>
                    )}
                  </div>
                  {(form.reference_images || []).length > 0 && (
                    <div className="space-y-3 pt-1" data-testid="ref-mode-section">
                      <RefModePicker value={form.ref_mode || "edit"} onChange={(m) => set("ref_mode", m)} />
                      {(form.ref_mode || "edit") !== "style" && (
                        <div className="grid md:grid-cols-2 gap-3">
                          <Field label={refModeText(lang).keep}>
                            <Textarea data-testid="input-ref-keep" rows={2} placeholder={refModeText(lang).keep_ph} value={form.ref_keep || ""} onChange={(e) => set("ref_keep", e.target.value)} className="bg-[#121218] border-white/10 focus:ring-orange-500" />
                          </Field>
                          <Field label={refModeText(lang).change}>
                            <Textarea data-testid="input-ref-change" rows={2} placeholder={refModeText(lang).change_ph} value={form.ref_change || ""} onChange={(e) => set("ref_change", e.target.value)} className="bg-[#121218] border-white/10 focus:ring-orange-500" />
                          </Field>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {outputType === "video" && (
                <div className="pt-4 border-t border-white/10 space-y-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold">{t("video_extra")}</div>
                  {user?.plan && user?.plan !== "pending" && (
                    <div data-testid="prompt-frames-section">
                      <div className="text-xs text-zinc-400 mb-2">{t("frames_prompt_hint")}</div>
                      <FramePicker start={form.video_frames?.start || null} end={form.video_frames?.end || null}
                        setStart={(f) => setFrame("start", f)} setEnd={(f) => setFrame("end", f)}
                        supportsStart supportsEnd uploader={uploadAsReference} tt={frameTT} />
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label={t("subj_motion")}><Input data-testid="input-mov-soggetto" placeholder={t("subj_motion_ph")} value={form.movimento_soggetto} onChange={(e) => set("movimento_soggetto", e.target.value)} className="bg-[#121218] border-white/10 focus:ring-orange-500" /></Field>
                    <Field label={t("cam_motion")}><Input data-testid="input-mov-camera" placeholder={t("cam_motion_ph")} value={form.movimento_camera} onChange={(e) => set("movimento_camera", e.target.value)} className="bg-[#121218] border-white/10 focus:ring-orange-500" /></Field>
                    <Field label={t("duration")}><Input data-testid="input-durata" placeholder={t("duration_ph")} value={form.durata} onChange={(e) => set("durata", e.target.value)} className="bg-[#121218] border-white/10 focus:ring-orange-500" /></Field>
                    <Field label={t("pace")}><Input data-testid="input-ritmo" placeholder={t("pace_ph")} value={form.ritmo} onChange={(e) => set("ritmo", e.target.value)} className="bg-[#121218] border-white/10 focus:ring-orange-500" /></Field>
                    <Field label={t("continuity")}><Input data-testid="input-continuita" placeholder={t("continuity_ph")} value={form.continuita} onChange={(e) => set("continuita", e.target.value)} className="bg-[#121218] border-white/10 focus:ring-orange-500" /></Field>
                    <Field label={t("dialog_audio")}><Input data-testid="input-dialogo" placeholder={t("dialog_ph")} value={form.dialogo_audio} onChange={(e) => set("dialogo_audio", e.target.value)} className="bg-[#121218] border-white/10 focus:ring-orange-500" /></Field>
                  </div>
                </div>
              )}

              {canUseAdvanced && (
                <div className="pt-4 border-t border-white/10 space-y-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold">{t("plus_pro_only")}</div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label={t("camera_type")}>
                      <Select value={form.camera_type} onValueChange={(v) => set("camera_type", v === "__none__" ? "" : v)}>
                        <SelectTrigger data-testid="select-camera-type" className="bg-[#121218] border-white/10">
                          <SelectValue placeholder={t("camera_ph")} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#16161D] border-white/10 max-h-72">
                          <SelectItem value="__none__">{t("camera_ph")}</SelectItem>
                          {CAMERA_PRESETS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label={t("lens_type")}>
                      <Select value={form.lens_type} onValueChange={(v) => set("lens_type", v === "__none__" ? "" : v)}>
                        <SelectTrigger data-testid="select-lens-type" className="bg-[#121218] border-white/10">
                          <SelectValue placeholder={t("lens_ph")} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#16161D] border-white/10 max-h-72">
                          <SelectItem value="__none__">{t("lens_ph")}</SelectItem>
                          {LENS_PRESETS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <Field label={t("negative_prompt")}>
                    <Textarea data-testid="input-negative" rows={2} placeholder={t("neg_ph")} value={form.negative_prompt} onChange={(e) => set("negative_prompt", e.target.value)} className="bg-[#121218] border-white/10 focus:ring-orange-500" />
                  </Field>
                  <Field label={t("tech_params")}>
                    <Input data-testid="input-tecnici" placeholder={t("tech_ph")} value={form.parametri_tecnici} onChange={(e) => set("parametri_tecnici", e.target.value)} className="bg-[#121218] border-white/10 focus:ring-orange-500" />
                  </Field>
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-6 rounded-md border border-white/10 bg-[#121218] p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-3">{t("output_prompt")}</div>
            <Button
              onClick={handleGenerateClick}
              disabled={loading}
              className="w-full h-12 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold mb-2 shadow-[0_0_40px_-10px_rgba(249,115,22,0.5)]"
              data-testid="generate-btn"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("generating")}</> : <><Sparkles className="w-4 h-4 mr-2" /> {t("generate")}</>}
            </Button>
            {(!canGenerate || overQuota) && !loading && (
              <div className="text-[11px] text-orange-400/90 mb-4 text-center leading-relaxed" data-testid="generate-hint">
                {overQuota
                  ? t("limit_msg")
                  : !form.obiettivo
                    ? "→ " + t("goal") + " " + t("required_field")
                    : !form.soggetto
                      ? "→ " + t("subject") + " " + t("required_field")
                      : !isPending && !modelId
                        ? "→ " + t("select_model_first")
                        : ""}
              </div>
            )}
            {(canGenerate && !overQuota) || loading ? <div className="mb-4" /> : null}
            {result ? (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{result.model_name}</div>
                <p className="text-sm text-zinc-200 leading-relaxed font-mono whitespace-pre-wrap break-words min-h-[120px]" data-testid="prompt-output-text">
                  {result.generated_prompt}
                </p>
                {!isPending && (
                  <div className="mt-4">
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1.5 block">{t("prompt_title") || "Title (optional)"}</label>
                    <Input
                      data-testid="prompt-title-input"
                      defaultValue={result.title || ""}
                      onBlur={(e) => saveTitle(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      maxLength={120}
                      placeholder={t("prompt_title_ph") || "Give this prompt a name to find it later"}
                      className="bg-[#121218] border-white/10 focus:ring-orange-500"
                    />
                  </div>
                )}
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => setGenOpen(true)} className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold" data-testid="generate-media-btn">
                    <Wand2 className="w-4 h-4 mr-2" /> {t("generate_media")}
                  </Button>
                  <Button onClick={copy} variant="outline" className="rounded-full border-white/10 text-white hover:bg-white/10 px-4" data-testid="copy-prompt-btn" title={t("copy")}>
                    <Copy className="w-4 h-4" />
                  </Button>
                  {!isPending && (
                    <Button onClick={toggleFav} variant="outline" className={`rounded-full border-white/10 ${result.is_favorite ? "text-orange-400" : "text-white"}`} data-testid="favorite-btn">
                      <Heart className={`w-4 h-4 ${result.is_favorite ? "fill-orange-400" : ""}`} />
                    </Button>
                  )}
                  {isPro && (
                    <Button onClick={exportJson} variant="outline" className="rounded-full border-white/10 text-white" data-testid="export-json-btn" title={t("export_json")}>
                      <Download className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <GenerateDialog
                  open={genOpen}
                  onOpenChange={setGenOpen}
                  promptText={result.generated_prompt}
                  promptId={result.prompt_id}
                  outputType={outputType}
                  editablePrompt={true}
                  initialRefs={(form.reference_images || []).map((r) => (r.image_id ? { kind: "ref", id: r.image_id } : r.generation_id ? { kind: "gallery", id: r.generation_id } : null)).filter(Boolean)}
                  initialStart={frameFromRefs("start_frame")}
                  initialEnd={frameFromRefs("end_frame")}
                  initialRefMode={form.ref_mode || "edit"}
                  initialAspect={form.formato || null}
                />
                {!isPending && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">{t("refine")}</div>
                    <Textarea
                      data-testid="refine-tweak-input"
                      rows={2}
                      value={tweak}
                      onChange={(e) => setTweak(e.target.value)}
                      placeholder={t("refine_placeholder")}
                      className="bg-[#121218] border-white/10 focus:ring-orange-500 text-sm mb-2"
                    />
                    <Button
                      onClick={refine}
                      disabled={refining || overQuota}
                      className="w-full rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/10 font-semibold"
                      data-testid="refine-btn"
                    >
                      {refining ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("generating")}</> : <><RefreshCw className="w-4 h-4 mr-2" /> {t("refine_btn")}</>}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-zinc-500 min-h-[80px] flex items-center justify-center border border-dashed border-white/10 rounded-md p-4 text-center">
                  {t("output_empty")}
                </div>
                {livePreview && (
                  <div data-testid="live-preview">
                    <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-2">{t("live_preview")}</div>
                    <div className="rounded-md border border-orange-500/20 bg-orange-500/[0.03] p-4 text-xs font-mono text-zinc-300 leading-relaxed whitespace-pre-wrap break-words max-h-[260px] overflow-y-auto">
                      {livePreview}
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-2 italic">{t("live_preview_hint")}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">
        {label}{required && <span className="text-orange-400 ml-1">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function AutoTextarea({ value, onChange, placeholder, testid, minRows = 2 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.max(el.scrollHeight, minRows * 24) + "px";
  }, [value, minRows]);
  return (
    <div>
      <Textarea
        ref={ref}
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={minRows}
        className="bg-[#121218] border-white/10 focus:ring-orange-500 resize-none overflow-hidden min-h-[52px] leading-relaxed"
      />
      <div className="mt-1 text-[10px] text-zinc-500 text-right font-mono" data-testid={`${testid}-count`}>
        {value.length} chars
      </div>
    </div>
  );
}


function RefImageThumb({ img }) {
  const [src, setSrc] = useState(img?.data || null);
  useEffect(() => {
    let blobUrl = null;
    let cancelled = false;
    if (img?.image_id || img?.generation_id) {
      (async () => {
        try {
          const res = await api.get(img.image_id ? `/reference-images/${img.image_id}` : `/generation/${img.generation_id}/thumbnail`, { responseType: "blob" });
          if (cancelled) return;
          blobUrl = URL.createObjectURL(res.data);
          setSrc(blobUrl);
        } catch { /* keep placeholder */ }
      })();
    } else if (img?.data) {
      setSrc(img.data);
    }
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [img?.image_id, img?.generation_id, img?.data]);
  if (!src) {
    return <div className="w-full h-24 bg-white/5 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-zinc-500" /></div>;
  }
  return <img src={src} alt="ref" className="w-full h-24 object-cover" />;
}
